import axios from "../../../../api/axios";

export const RM_VENDOR_ENDPOINTS = {
  BASE: "v2/rm-vendors",
  GENERATE_OTP: "v2/rm-vendors/generate-otp",
  GENERATE_PASSWORD_RESET: "v2/rm-vendors/generate-password-reset",
  UPLOAD_IMAGE: "v2/rm-vendors/upload-image",
  GET_IMAGE_SIGNED_URL: "v2/rm-vendors/image-signed-url",
  GET_REQUEST_TYPES: "requesttype/getrequest",
};

/**
 * Central error handler
 */
const handleApiError = (error, defaultMessage) => {
  const message = error?.response?.data?.message || error.message || defaultMessage;
  throw new Error(message);
};

/**
 * Get all RM Vendors
 * @param {string} [propertyId] - Optional property ID to filter vendors
 */
export const getAllRMVendors = async (propertyId) => {
  try {
    const endpoint = propertyId
      ? `${RM_VENDOR_ENDPOINTS.BASE}?property_id=${encodeURIComponent(propertyId)}`
      : RM_VENDOR_ENDPOINTS.BASE;

    const { data } = await axios.get(endpoint);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to fetch RM vendors");
  }
};

/**
 * Create a new RM Vendor
 */
export const createRMVendor = async (payload) => {
  try {
    const { data } = await axios.post(RM_VENDOR_ENDPOINTS.BASE, payload);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to create RM vendor");
  }
};

/**
 * Update an existing RM Vendor
 */
export const updateRMVendor = async (id, payload) => {
  try {
    const { data } = await axios.put(`${RM_VENDOR_ENDPOINTS.BASE}/${id}`, payload);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to update RM vendor");
  }
};

/**
 * Delete an RM Vendor
 */
export const deleteRMVendor = async (id) => {
  try {
    const { data } = await axios.delete(`${RM_VENDOR_ENDPOINTS.BASE}/${id}`);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to delete RM vendor");
  }
};

/**
 * Generate OTP for an RM Vendor
 */
export const generateVendorOTP = async (id) => {
  try {
    const { data } = await axios.post(`${RM_VENDOR_ENDPOINTS.GENERATE_OTP}/${id}`);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to generate OTP");
  }
};

/**
 * Generate Password Reset for an RM Vendor
 */
export const generatePasswordReset = async (id) => {
  try {
    const { data } = await axios.post(`${RM_VENDOR_ENDPOINTS.GENERATE_PASSWORD_RESET}/${id}`);
    return data;
  } catch (error) {
    handleApiError(error, "Failed to generate password reset token");
  }
};

/**
 * Upload image for an RM Vendor
 */
export const uploadRMVendorItemImage = async (formData, onUploadProgress) => {
  try {
    const { data } = await axios.post(RM_VENDOR_ENDPOINTS.UPLOAD_IMAGE, formData, {
      onUploadProgress,
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  } catch (error) {
    handleApiError(error, "Failed to upload image");
  }
};

/**
 * Get signed URL for RM Vendor image
 */
export const getImageSignedUrl = async (fileUrl) => {
  try {
    const { data } = await axios.get(RM_VENDOR_ENDPOINTS.GET_IMAGE_SIGNED_URL, {
      params: { fileUrl },
    });
    return data.data;
  } catch (error) {
    handleApiError(error, "Failed to get signed URL for image");
  }
};

/**
 * Get Request Types by Company ID
 */
export const getRequestTypes = async (company_id) => {
  try {
    const response = await axios.post(RM_VENDOR_ENDPOINTS.GET_REQUEST_TYPES, {
      company_id,
    });
    const requestTypes = response.data?.requestType || [];
    return requestTypes.reverse(); // Return reversed list
  } catch (error) {
    handleApiError(error, "Failed to fetch RM vendor request types");
  }
};
